<template>
  <Card class="p-0 border-none bg-transparent">
    <form action="#" @submit.prevent="handleSubmit">
      <GiftcardAmountPicker
        ref="amountPicker"
        v-model="selectedAmount"
        v-model:quantity="selectedQuantity"
        v-model:invalid="isAmountInvalid"
        :presets="amounts"
        :min="minimalAmount"
        :max="maximumAmount"
        :placeholder="t('giftcard.hero.customPlaceholder')"
        :presets-label="t('giftcard.select')"
        :helper-text="t('giftcard.hero.customHelper', amountRange)"
        :range-error-message="t('giftcard.hero.customError', amountRange)"
        variant="hero"
        stacked
        :required="isSubdomain"
      >
        <template #presets-label>
          <span
            class="mb-3 inline-flex text-sm font-semibold uppercase tracking-wider text-teal-900"
          >
            {{ t('giftcard.select') }}
          </span>
        </template>
      </GiftcardAmountPicker>

      <Button
        class="mt-6 flex w-full items-center justify-center gap-2 py-4 font-semibold"
        data-test-id="buy-giftcard"
        type="submit"
        :disabled="isAmountInvalid"
      >
        {{ ctaLabel }}
        <ArrowRight :size="18" class="shrink-0" />
      </Button>
      <!-- the subdomain flow adds straight to cart, so there's no next step -->
      <div
        v-if="!isSubdomain"
        data-test-id="buy-giftcard-note"
        class="mt-2 text-sm text-stone-700"
      >
        {{ t('giftcard.hero.nextStepNote') }}
      </div>
    </form>
  </Card>
</template>

<script lang="ts" setup>
import { ArrowRight } from 'lucide-vue-next'
import GiftcardAmountPicker from '~/components/giftcard/GiftcardAmountPicker.vue'
import {
  GIFTCARD_MAX_AMOUNT,
  GIFTCARD_MIN_AMOUNT,
  resolveGiftcardAmountFromQuery,
} from '~/utils/giftcardAmount'

const { t } = useI18n()
const $localePath = useLocalePath()
const route = useRoute()
const { addItem } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState('general-instance')
const { pricingsFactory } = usePricingsFactory()

const isSubdomain = useIsSubdomain()
const subdomain = useSubdomain()
const { data: partnerData } = subdomain.value
  ? await useSubdomainData()
  : { data: ref(undefined) }

const supplierId = computed(() => supplierGetters.getId(partnerData.value))
const supplierName = computed(() => supplierGetters.getName(partnerData.value))

const props = defineProps<{
  isMagazine?: boolean
}>()

const getSupplierName = computed(() => {
  if (supplierName.value) return supplierName.value
  if (props.isMagazine) return 'konfetti magazine'
  return 'konfetti'
})

// Available options
const amounts = ref([
  parseInt(t('giftcard.option_1')),
  parseInt(t('giftcard.option_2')),
  parseInt(t('giftcard.option_3')),
  parseInt(t('giftcard.option_4')),
])
const minimalAmount = GIFTCARD_MIN_AMOUNT
const maximumAmount = GIFTCARD_MAX_AMOUNT
const amountRange = { min: minimalAmount, max: maximumAmount }

// start on the €50 chip; the custom field stays empty beside it (the hero
// picker keeps chips and field exclusive), so it still reads as an option
const DEFAULT_AMOUNT = 50
const selectedAmount = ref<number | null>(DEFAULT_AMOUNT)
const selectedQuantity = ref<number>(1)
const isAmountInvalid = ref(false)
const amountPicker = ref<{ checkError: () => boolean } | null>(null)

// on a subdomain this button adds straight to cart rather than routing to
// the personalization page, so the label needs to say so
const ctaLabel = computed(() =>
  isSubdomain.value ? t('giftcard.bookAction') : t('giftcard.personalizeAction')
)

// Initialize from query parameter on client side
onMounted(() => {
  const queryAmount = resolveGiftcardAmountFromQuery(route.query.amount, {
    min: minimalAmount,
    max: maximumAmount,
  })
  if (queryAmount !== null) selectedAmount.value = queryAmount
})

// On a booking-solution subdomain, keep the original flow: add the giftcard
// to the cart directly, skipping the personalization page entirely (that
// flow isn't wired up for embedded/booking-solution contexts). Everywhere
// else, carry the selected amount (if any) to the personalization page via
// the `amount` query param, which prefills its amount field - with nothing
// chosen it just opens the personalization page, which asks for the amount.
const handleSubmit = async () => {
  if (!amountPicker.value?.checkError()) return
  const amount = selectedAmount.value

  if (isSubdomain.value) {
    // the picker is required here, so checkError() already rejected null
    if (amount === null) return

    const cartItem = parseGiftcardWithoutEvent(
      pricingsFactory(amount * 100, {
        totalParticipants: 1,
      }),
      t('cart.giftcardTitle'),
      supplierId.value,
      'giftcard_' + amount,
      getSupplierName.value
    )

    await addItem(cartItem)
    toggleCartSidebar()
    return
  }

  await navigateTo(
    $localePath({
      path: '/giftcard/personalize',
      query: amount === null ? undefined : { amount: String(amount) },
    })
  )
}
</script>

<style lang="css" scoped>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
